import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Heading, HStack, SimpleGrid, useDisclosure } from '@chakra-ui/react';

import { FeatureName } from '__generated__/GQL';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import { useCanCreateAdditionalWallet } from 'common/hooks/useCanCreateAdditionalWallet';
import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import { useMultiWalletFeature } from 'common/hooks/useMultiWalletFeature';
import Button from 'components/_core/Button';
import Tooltip from 'components/_core/Tooltip';
import { BankAccountIllustrationName } from 'components/BankAccountIcon';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import MultiWalletUpsellModal from 'components/MultiWalletUpsellModal';
import { logEvent } from 'features/Analytics/analytics';
import TrackView from 'features/Analytics/TrackView';
import * as routes from 'features/Bank/routes';
import * as ContentLayout from 'layouts/ContentLayout';

import BankAccountCard from './components/BankAccountCard';
import MultiWalletUpsellCard from './components/MultiWalletUpsellCard';
import locales from './locales';

const BankAccounts: FC = () => {
  const { isMultiWalletFeatureAvailableForCompanyPlan } =
    useMultiWalletFeature();
  const { bankAccounts, error, loading } = useCompanyBankAccounts();
  const {
    description: canCreateAdditionalWalletDescription,
    value: canCreateAdditionalWallet,
  } = useCanCreateAdditionalWallet();
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { plan } = useCurrentPricingPlan();

  if (error) {
    return <ErrorView />;
  }

  if (loading || !plan) {
    return <Loader />;
  }

  const {
    limits: { activeBankAccounts },
    upgradablePlanIds,
  } = plan;
  const canAccessUpgrade = upgradablePlanIds?.length > 0;
  const walletLimit = activeBankAccounts.value - 1;

  return (
    <ContentLayout.Root>
      <ContentLayout.Body size="md">
        <TrackView pageName="BankAccountsPage" />
        <HStack justifyContent="space-between" marginBottom="space-40">
          <Heading size="lg">{locales.title}</Heading>
          {canAccessFeature({ name: FeatureName.Bank }) &&
          isMultiWalletFeatureAvailableForCompanyPlan ? (
            <HStack spacing="space-16">
              <Button
                as="a"
                href="https://shinetools.typeform.com/to/BKFqI8eR"
                icon="link"
                iconPosition="right"
                target="_blank"
                variant="secondary"
              >
                {locales.feedback}
              </Button>
              <Tooltip
                closeOnClick={false}
                description={canCreateAdditionalWalletDescription}
                isDisabled={canCreateAdditionalWallet || canAccessUpgrade}
                placement="bottom"
              >
                <Button
                  icon="plus"
                  iconPosition="left"
                  isDisabled={!canCreateAdditionalWallet && !canAccessUpgrade}
                  {...(canCreateAdditionalWallet
                    ? { as: Link, to: routes.newBankAccount }
                    : {})}
                  variant="primary"
                  {...(canAccessUpgrade && {
                    onClick: () => {
                      onOpen();
                      logEvent({
                        name: 'Open Multi Wallet Upsell Modal',
                        properties: {
                          plan: plan.brandName,
                        },
                      });
                    },
                  })}
                >
                  {locales.createAccountLinkLabel}
                </Button>
              </Tooltip>
            </HStack>
          ) : null}
        </HStack>
        <SimpleGrid columns={3} spacing="space-16">
          {bankAccounts.map(
            ({
              bankAccountId,
              currency,
              currentBalance,
              illustrationName,
              name,
              type,
            }) => (
              <BankAccountCard
                accountType={type}
                balance={currentBalance / 100}
                bankAccountId={bankAccountId}
                currency={currency}
                illustrationName={
                  illustrationName as BankAccountIllustrationName | null
                }
                key={bankAccountId}
                name={name}
              />
            ),
          )}
          {canAccessFeature({ name: FeatureName.Bank }) &&
          !isMultiWalletFeatureAvailableForCompanyPlan ? (
            <MultiWalletUpsellCard />
          ) : null}
        </SimpleGrid>
        {isOpen && canAccessUpgrade ? (
          <MultiWalletUpsellModal
            isOpen={isOpen}
            onClose={onClose}
            walletLimit={walletLimit}
          />
        ) : null}
      </ContentLayout.Body>
    </ContentLayout.Root>
  );
};

export default BankAccounts;
